<template>
	<div
	class="card-element"
	:class="{
		'selected': params.id === selectedCardId,
		'dark-theme': getAppThemeMode === 'dark',
        'mobile-slider': mobileSlider,
		'previous-card': previousCard,
		'hide-previous-card': hidePreviousCards,
		'next-card': nextCard,
		'medium-card': dashboardWidget,
		'bigger-card': dashboardWidgetAnchor,
		'mobile': getMobileAppState
	}"
	@click="emit('clicked', params.id)">
        <div v-show="!cardImageIsLoaded" class="placeholder-for-image">
            <div class="fake-text-1"></div>
            <div class="fake-text-2"></div>
            <div class="fake-text-3-4">
                <div class="fake-text-3"></div>
                <div class="fake-text-4"></div>
            </div>
            <div class="fake-text-5"></div>
        </div>
		<!-- Image -->
		<transition name="image-animation" mode="out-in" appear>
            <img
            v-if="params.id === selectedCardId || previousCard || nextCard"
            v-show="cardImageIsLoaded"
            class="image"
            :src="cardImage"
            @error="imageErrorHandler"
            @load="imageLoadHandler"/>
        </transition>
		<!-- Negative Status -->
		<div
        v-if="cardNegativeStatusMapper(params.status) || params.unblockable"
        class="negative-status-layer"
        :class="{'no-background-color': !cardImageIsLoaded}">
			<div v-if="params.unblockable" class="chip-status"><icon-lock/>{{$t('cards.blockedStatus')}}</div>
			<div v-else-if="params.status === 'GENERATING'" class="chip-status"><icon-lock/>{{params.statusDescriptive}}</div>
			<div v-else class="chip-status"><icon-status-line-alert/>{{params.statusDescriptive}}</div>
		</div>
	</div>
</template>

<script setup>
/* Vue */
import { ref, watch } from 'vue'
/* Store */
import { mapGetters } from '@/store/helpers/mappers'
/* Helpers */
import { createCardImageUrl } from '@/network/api/cards/cards.api.js'
import { cardNegativeStatusMapper } from '@/assets/scripts/mappers/cards'

const { getMobileAppState, getAppThemeMode } = mapGetters()
/* Emmitters */
const emit = defineEmits(['clicked'])

/* Props
@mobileSlider {Boolean} - prop to apply mobile slider styles for this card
@dashboardWidget {Boolean} - prop to apply logics for this to be used on dashboardWidget
@dashboardWidgetAnchor {Boolean} -  prop to apply logics for this to be used on dashboardWidget Anchor
@selectedCardId {Number} - card id of selected card
@params {Object} - object with card info, needs to have:
	- id {Number, String}
	- type {String}
	- productName {String}
	- product {String}
	- status {String} - code of card status
	- statusDescriptive {String} - description of current status
	- blockable {Boolean} - if card can be blocked, it means the card is currently unblocked
	- unblockable {Boolean} - if card can be unblocked, it means the card is currently blocked
@hideBalance {Boolean} - flag to hide/show image with balances
*/
const props = defineProps({
    mobileSlider: Boolean,
	dashboardWidget: Boolean,
	dashboardWidgetAnchor: Boolean,
	selectedCardId: [Number, String],
	params: Object,
	previousCard: Boolean,
	nextCard: Boolean,
	hidePreviousCards: Boolean,
    hideBalance: Boolean
})

const cardImageIsLoaded = ref(false)
const cardImage = ref((props.params.cardImageUrl ? props.params.cardImageUrl : createCardImageUrl(props.params.id)) + `&hideBalance=${props.hideBalance}`)

/** validates if image is loaded
@return { void }
*/
const imageLoadHandler = () => {
    cardImageIsLoaded.value = true
}

/** hangles image load error
@return { void }
*/
const imageErrorHandler = () => {
	cardImageIsLoaded.value = false
}

watch(() => props.hideBalance, (newVal, oldVal) => {
    cardImageIsLoaded.value = false
    cardImage.value = (props.params.cardImageUrl ? props.params.cardImageUrl : createCardImageUrl(props.params.id)) + `&hideBalance=${props.hideBalance}`
})
</script>

<style lang="scss" scoped>
.card-element {
	width: 134px;
	height: 80px;
	position: relative;

	flex-shrink: 0;
	border-radius: 8.4px;

	transition: all .3s ease-in-out .1s;
    overflow: hidden;

	cursor: pointer;

    background: var(--gradient-lazy-loading);

    .placeholder-for-image {
        width: 100%;
        height: 100%;

        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 15% 12px 0 12px;

        .fake-text-1,
        .fake-text-2,
        .fake-text-3,
        .fake-text-4,
        .fake-text-5 {
            @include lazyLoadingCardsAnimation;
            width: 90%;
            position: relative;
            overflow: hidden;
        }

        .fake-text-1 {
            width: 80%;
            height: 8px;
            margin-bottom: 8px;
            border-radius: 8px;
        }

        .fake-text-2 {
            width: 50%;
            height: 6px;
            border-radius: 6px;
        }

        .fake-text-3-4 {
            width: 50%;
            display: flex;
            gap: 12px;

            .fake-text-3 {
                width: 70%;
                height: 6px;
                border-radius: 6px;
            }

            .fake-text-4 {
                width: calc(30% - 12px);
                height: 6px;
                border-radius: 6px;
            }
        }

        .fake-text-5 {
            width: 50%;
            height: 6px;
            border-radius: 6px;
        }
    }

    &.bigger-card .placeholder-for-image {
        padding: 25% 12px 0 12px;
    }

    &:not(.mobile-slider) {
        &.previous-card {
            transform: translateX(10px);
        }

        &.next-card {
            transform: translateX(-10px);
        }

        &.bigger-card {
            width: 226px;
            height: 136px;

            border-radius: 14px;

            &.previous-card {
                transition: all .2s ease-in-out;

                &.hide-previous-card {
                    opacity: 0;
                }
            }

            &.selected {
                width: 318px;
                height: 188px;

                .image {
                    opacity: 1;
                }
            }

            &.next-card {
                .image {
                    opacity: 0.24;
                }
            }

            &.mobile {
                width: 120px;
                height: 72px;

                &.selected {
                    width: 170px;
                    height: 102px;
                }
            }
        }

        &.medium-card {
            width: 160px;
            height: 98px;
            border-radius: 8px;

            &.previous-card {
                transition: all .2s ease-in-out;

                &.hide-previous-card {
                    opacity: 0;
                }
            }

            &.selected {
                width: 226px;
                height: 136px;

                .image {
                    opacity: 1;
                }
            }

            &.next-card {
                .image {
                    opacity: 0.24;
                }
            }

            &.mobile {
                width: 120px;
                height: 72px;

                &.selected {
                    width: 170px;
                    height: 102px;
                }
            }
        }
    }

	&.selected {
		width: 187px;
		height: 112px;
		border-radius: 8px;
		box-shadow: 0px 8px 24px 0px rgba(0, 0, 0, 0.32);
		z-index: 1;

        .image {
            opacity: 1;
        }

		.negative-status-layer .chip-status {
			height: 32px;

			svg {
				width: 20px;
				height: 20px;
			}
		}
	}

	.image {
		width: 100%;
		height: 100%;

        object-fit: cover;
		overflow: hidden;

        opacity: 0.24;
        transition: opacity 0.2s linear;

        &.image-animation-enter-active,
        &.image-animation-leave-active {
            transition: opacity 0.2s linear;
        }

        &.image-animation-enter-from,
        &.image-animation-leave-to {
            opacity: 0;
        }
	}

	.negative-status-layer {
		width: 100%;
		height: 100%;

		display: flex;
		align-items: center;
		justify-content: center;

		position: absolute;
		top: 0;
		left: 0;
		z-index: 2;

        &:not(.no-background-color) {
            &:before {
                content: '';
                width: 100%;
                height: 100%;

                position: absolute;

                opacity: 0.54;
                border-radius: 11px;
                background-color: var(--secondary-dark-4);
            }
        }

		.chip-status {
			height: 21px;
			padding: 0 16px;

			display: flex;
			align-items: center;

			@include H7-Regular;
			color: var(--secondary-light-4);
			text-transform: uppercase;

			border-radius: 8px;
			background-color: var(--secondary-dark-4);
			z-index: 1;

			svg {
				width: 13px;
				height: 13px;
				margin-right: 6px;

				.change-fill-color {
					fill: var(--secondary-light-4);
				}
			}
		}
	}

    &.mobile-slider {
        width: 214px;
        height: 128px;

        &.selected {
            width: 240px;
            height: 144px;
            box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.25);
        }
    }
}
</style>
